import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useForm, SubmitHandler } from "react-hook-form";
import { useContext, useEffect, useMemo, useState } from "react";

import Header from "@/components/header/Header";
import { Button } from "@/components/ui/button";
import CredentialsForm from "@/components/Form/Form";
import ConsentList from "@/components/consent-list/ConsentList";
import ButtonContainer from "@/components/button-container";
import SimpleErrorMessage from "@/components/error-message/new-error-message";

import { genericService } from "@/services/GenericService";

import { AuthFlowType, FLOW_CONFIGS, FlowConfig } from "@/config/ConsentsFlowConfig";
import { DigitalAssetContext } from "@/contexts/continente-credentials/DigitalAssetContext";

import { resolveClientTopLogo } from "@/utils/clientBranding";
import { tryCatch } from "@/utils/try-catch";

import { useApiResponseHandler } from "@/hooks/useApiResponseHandler";

import { FormError } from "@/models/FormError";
import { ConsentsResponse } from "@/models/continente-credentials/mobile/ConsentsResponse";
import { ConsentAnswerRequest, ConsentAuthorizationRequest } from "@/models/shared/consents/ConsentAuthorizationRequest";

interface SharedConsentScreenProps {
  flowConfig: FlowConfig;
}

type Form = ConsentAuthorizationRequest & FormError;

export default function SharedConsentAuthorizationScreen({ flowConfig }: SharedConsentScreenProps) {
  const { t } = useTranslation();
  const location = useLocation<ConsentsResponse>();
  const digitalAssetContext = useContext(DigitalAssetContext);

  const consentsPayload = location.state?.consents;
  const legacyProperties = location.state?.properties;
  const hasConsentsInState = consentsPayload?.items != null || legacyProperties?.consents != null;
  const [loading, setLoading] = useState(!hasConsentsInState);
  const { handleResponse } = useApiResponseHandler();

  const nextStep = location.state?.nextStep ?? "api" + location.pathname;

  const clientName = consentsPayload?.clientName ?? legacyProperties?.clientName;
  const consents = consentsPayload?.items ?? legacyProperties?.consents;

  const mandatoryConsents = useMemo(
    () => consents?.filter((c) => c.isMandatory),
    [consents]
  );
  const optionalConsents = useMemo(
    () => consents?.filter((c) => !c.isMandatory),
    [consents]
  );

  // Os obrigatórios estão sempre autorizados; os opcionais vêm autorizados por defeito.
  const [selectedOptional, setSelectedOptional] = useState<Record<string, boolean>>({});

  useEffect(() => {
    setSelectedOptional(Object.fromEntries(optionalConsents?.map((c) => [c.id, true]) ?? []));
  }, [optionalConsents]);

  const formState = useForm<Form>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
  });

  const {
    register,
    handleSubmit,
    setError,
    setValue,
    clearErrors,
    formState: { errors },
  } = formState;

  const toggleOptional = (id: string) =>
    setSelectedOptional((prev) => ({ ...prev, [id]: !prev[id] }));

  // Não é possível recusar quando existe pelo menos um consentimento obrigatório.
  const canDecline = mandatoryConsents?.length === 0;
  const allSelected = optionalConsents?.every((c) => selectedOptional[c.id]);

  const acceptLabels = !allSelected
    ? { label: "consent.accept_selected", aria: "consent.accept_selected_aria" }
    : (consents?.length ?? 0) > 1
      ? { label: "consent.accept_all", aria: "consent.accept_all_aria" }
      : { label: "consent.accept", aria: "consent.accept_aria" };

  const topLogo = useMemo(() => resolveClientTopLogo(digitalAssetContext.digitalAsset.clientLogo), []);

  const buildConsentAnswers = (answer: boolean): Array<ConsentAnswerRequest> =>
    (consents ?? []).map((c) => ({
      id: c.id,
      answer: answer && (c.isMandatory || !!selectedOptional[c.id]),
    }));

  const onSubmit: SubmitHandler<ConsentAuthorizationRequest> = async (data) => {
    setLoading(true);
    const { data: response, error } = await tryCatch(genericService.post<ConsentsResponse, ConsentAuthorizationRequest>(
      nextStep,
      {
        consents: data.consents,
      },
    ));

    handleResponse(response, {
      setError
    }, error);

    setLoading(false);
  };

  const handleChooseOption = (answer: boolean) => {
    setValue("consents", buildConsentAnswers(answer));
    clearErrors();
    handleSubmit(onSubmit)();
  };

  return (
    <CredentialsForm
      id={flowConfig.form}
      form={formState}
      autoComplete="off"
      onSubmit={(e) => {
        clearErrors();
        handleSubmit(onSubmit)(e);
      }}
    >
      <Header
        title={t(flowConfig.labels.headerTitle)}
        subtitle={t(flowConfig.labels.headerSubtitle, {
          clientName: clientName,
        })}
        showLoading={loading}
        showBackButton={false}
        topLogo={topLogo}
      />
      <div className="flex flex-col py-4 gap-12 w-full">
        <ConsentList
          title={t("consent.mandatory_section")}
          consents={mandatoryConsents}
        />

        <ConsentList
          title={t("consent.optional_section")}
          consents={optionalConsents}
          onToggle={toggleOptional}
        />

      </div>
      <div>
        <span>
          {t("consent.footer_text.1")}
          <a className="font-bold underline text-[var(--on-surface-primary-default-variant)] hover:text-[var(--on-surface-primary-hover-variant)] pressed:text-[var(--on-surface-primary-pressed-variant)] disabled:text-[var(--on-surface-variant-2)]">{t("consent.footer_text.2")}</a>
          {t("consent.footer_text.3")}
        </span>
      </div>
      <div>
        <span>
          {t("consent.know_more.1")}
          <a className="font-bold underline text-[var(--on-surface-primary-default-variant)] hover:text-[var(--on-surface-primary-hover-variant)] pressed:text-[var(--on-surface-primary-pressed-variant)] disabled:text-[var(--on-surface-variant-2)]">{t("consent.know_more.2")}</a>
        </span>
      </div>
      {errors.submitError && <SimpleErrorMessage error={errors.submitError.message} />}
      <ButtonContainer className="mt-8 pb-8">
        <Button
          type="button"
          disabled={loading}
          onClick={() => handleChooseOption(true)}
          aria-label={t(acceptLabels.aria)}
        >
          {t(acceptLabels.label)}
        </Button>
        <Button
          variant="secondary"
          type="button"
          disabled={loading || !canDecline}
          onClick={() => handleChooseOption(false)}
          aria-label={canDecline ? t("consent.decline_aria") : t("consent.decline_disabled_aria")}
        >
          {t("consent.decline")}
        </Button>
      </ButtonContainer>
    </CredentialsForm>
  )
}

export const createConsentAuthorizationPage = (flowConfig: AuthFlowType) => {
  const config = FLOW_CONFIGS[flowConfig];
  return () => <SharedConsentAuthorizationScreen flowConfig={config} />;
};

export const ConsentAuthorization = createConsentAuthorizationPage(AuthFlowType.CREDENTIALS);
export const SCConsentAuthorization = createConsentAuthorizationPage(AuthFlowType.SITE_CREDENTIALS);